import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { Phone, Mail, MessageSquare, MapPin, Globe, Facebook } from 'lucide-react';
import { Button } from '../ui/button';
import { toast } from 'sonner';

export const ContactInfo = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  const handleCopyEmail = () => {
    navigator.clipboard.writeText('vtvlanka@gmail.com');
    toast.success('Email copied to clipboard!');
  };

  const phoneNumbers = [
    { label: 'Hot Line (24/7)', number: '+94 70 400 30 30', type: 'hotline' },
    { label: 'Land Line', number: '+94 11 490 50 50', type: 'landline' },
    { label: 'Mobile', number: '+94 77 88 66 444', type: 'main' },
  ];

  const contactMethods = [
    {
      icon: Mail,
      title: 'Email',
      value: 'vtvlanka@gmail.com',
      action: () => window.location.href = 'mailto:vtvlanka@gmail.com',
      actionLabel: 'Send Email',
      color: 'accent',
    },
    {
      icon: MessageSquare,
      title: 'WhatsApp',
      value: '+94 70 400 30 30',
      action: () => window.open('https://wa.me/94704003030', '_blank'),
      actionLabel: 'Chat Now',
      color: 'primary',
    },
    {
      icon: Globe,
      title: 'Website',
      value: 'www.vtvlanka.com',
      action: () => window.open('https://www.vtvlanka.com', '_blank'),
      actionLabel: 'Visit Website',
      color: 'accent',
    },
    {
      icon: Facebook,
      title: 'Facebook',
      value: 'Follow us on Facebook',
      action: () => window.open('https://www.facebook.com/vtventerprises', '_blank'),
      actionLabel: 'Follow Us',
      color: 'primary',
    },
  ];

  return (
    <section ref={ref} className="py-10 sm:py-14">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Phone Numbers Grid */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
          transition={{ duration: 0.6 }}
          className="mb-16"
        >
          <h2 className="text-3xl sm:text-4xl font-bold text-center mb-4">
            <span className="gradient-text-blue">Phone Numbers</span>
          </h2>
          <p className="text-center text-muted-foreground mb-12">Click to call directly from your device</p>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {phoneNumbers.map((phone, index) => (
              <motion.a
                key={phone.number}
                href={`tel:${phone.number.replace(/\s/g, '')}`}
                initial={{ opacity: 0, y: 20 }}
                animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
                transition={{ duration: 0.5, delay: index * 0.05 }}
                className="glass-card p-6 rounded-xl hover-lift group"
              >
                <div className="flex items-center gap-4">
                  <div className="p-3 rounded-full bg-primary/10 group-hover:bg-primary/20 transition-colors">
                    <Phone className="h-6 w-6 text-primary" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm text-muted-foreground mb-1">{phone.label}</p>
                    <p className="font-semibold text-foreground group-hover:text-primary transition-colors">
                      {phone.number}
                    </p>
                  </div>
                </div>
              </motion.a>
            ))}
          </div>
        </motion.div>

        {/* Contact Methods */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="mb-16"
        >
          <h2 className="text-3xl sm:text-4xl font-bold text-center mb-12">
            Other <span className="gradient-text-blue">Contact Methods</span>
          </h2>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {contactMethods.map((method, index) => {
              const Icon = method.icon;
              return (
                <motion.div
                  key={method.title}
                  initial={{ opacity: 0, y: 20 }}
                  animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
                  transition={{ duration: 0.5, delay: 0.3 + index * 0.1 }}
                  className="metallic-card p-6 rounded-xl group"
                >
                  <div className="flex flex-col items-center text-center space-y-4">
                    <div
                      className={`p-4 rounded-full ${method.color === 'primary'
                        ? 'bg-primary/10 group-hover:bg-primary/20'
                        : 'bg-accent/10 group-hover:bg-accent/20'
                        } transition-colors`}
                    >
                      <Icon
                        className={`h-8 w-8 ${method.color === 'primary' ? 'text-primary' : 'text-accent'
                          }`}
                      />
                    </div>
                    <div>
                      <h3 className="font-semibold mb-2">{method.title}</h3>
                      <p className="text-sm text-muted-foreground mb-4">{method.value}</p>
                    </div>
                    <Button
                      onClick={method.action}
                      size="sm"
                      className={`w-full ${method.color === 'primary'
                        ? 'bg-primary hover:bg-primary/90'
                        : 'bg-accent hover:bg-accent-dark'
                        }`}
                    >
                      {method.actionLabel}
                    </Button>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </motion.div>

        {/* Address */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
          transition={{ duration: 0.6, delay: 0.6 }}
          className="max-w-2xl mx-auto glass-card p-8 sm:p-12 rounded-2xl text-center"
        >
          <div className="flex justify-center mb-6">
            <div className="p-4 rounded-full bg-primary/10">
              <MapPin className="h-10 w-10 text-primary" />
            </div>
          </div>
          <h3 className="text-2xl font-bold mb-4">Visit Our Office</h3>
          <p className="text-lg text-foreground mb-2 font-medium">
            No. 19, Murugan Kovil Road
          </p>
          <p className="text-muted-foreground">
            Nelliady, Northern,<br />
            Sri Lanka
          </p>
        </motion.div>
      </div>
    </section>
  );
};
